/**
 * Single source of truth for the in-portal docs: sidebar order, route slugs and prev/next links.
 * Every slug must have a matching server/src/main/resources/docs/<slug>.md (docsManifest.test.ts
 * checks this).
 */
export interface DocEntry {
  slug: string;
  title: string;
  children?: DocEntry[];
}

export const DOCS_BASE_PATH = "/home/docs";

export const docsManifest: DocEntry[] = [
  { slug: "getting-started", title: "Getting Started with Orkes Cloud" },
  { slug: "team-and-access", title: "Setting Up Your Team & Access" },
  {
    slug: "deployment",
    title: "Deploying Your Cluster",
    children: [
      {
        slug: "deployment/orkes-hosted-setup",
        title: "Deployment Guide: Orkes Hosted Cluster Setup",
      },
      {
        slug: "deployment/architecture",
        title: "Architecture: Customer Hosted Cloud Deployment",
      },
      {
        slug: "deployment/aws",
        title: "How to deploy a customer-hosted cluster on AWS",
      },
      {
        slug: "deployment/azure",
        title: "How to deploy a customer-hosted cluster on Azure",
      },
      {
        slug: "deployment/gcp",
        title: "How to deploy a customer-hosted cluster on GCP",
      },
      {
        slug: "deployment/hosted-worker-setup",
        title: "Hosted worker setup guide on Azure",
      },
    ],
  },
  {
    slug: "managing-your-cluster",
    title: "Managing Your Cluster",
    children: [
      {
        slug: "managing-your-cluster/custom-certificates",
        title: "Manage Custom Certificates",
      },
      {
        slug: "managing-your-cluster/upgrade-rollback-restart",
        title: "Upgrade, Roll Back, and Restart Deployments",
      },
      {
        slug: "managing-your-cluster/details-protection-deletion",
        title: "Manage Cluster Details, Protection, and Deletion",
      },
      {
        slug: "managing-your-cluster/auth-domains-networking",
        title: "Manage Cluster Authentication, Domains & Networking",
      },
    ],
  },
  {
    slug: "monitoring-your-cluster",
    title: "Monitoring Your Cluster",
    children: [
      {
        slug: "monitoring-your-cluster/exporting-metrics",
        title: "Exporting Advanced Metrics to an External Monitoring Platform",
      },
    ],
  },
  {
    slug: "sso",
    title: "Single Sign-On for Conductor Clusters",
    children: [
      {
        slug: "sso/entra-id",
        title: "Configure Single-Sign-On (SSO) with Microsoft Entra ID",
      },
      { slug: "sso/okta", title: "Configure Single-Sign-On (SSO) with Okta" },
      {
        slug: "sso/oidc",
        title: "Configure Single-Sign-On (SSO) with OpenID Connect (OIDC)",
      },
      { slug: "sso/azure-federation", title: "Configure Azure Federation" },
    ],
  },
  {
    slug: "networking",
    title: "Networking & Connectivity",
    children: [
      {
        slug: "networking/aws-privatelink",
        title: "Connect Conductor Cluster with AWS PrivateLink",
      },
      {
        slug: "networking/azure-private-link",
        title: "Connect Conductor Cluster with Azure Private Link",
      },
      {
        slug: "networking/gcp-private-service-connect",
        title: "Connect Conductor Cluster with GCP Private Service Connect",
      },
      {
        slug: "networking/gcp-vpc-peering",
        title: "Connect Conductor Cluster with GCP VPC Peering",
      },
      {
        slug: "networking/http-worker-proxy",
        title: "Configure Proxy for HTTP Workers",
      },
    ],
  },
];

/** Depth-first, the order a reader pages through with prev/next. */
export const flattenDocs = (entries: DocEntry[]): DocEntry[] =>
  entries.flatMap((entry) => [entry, ...flattenDocs(entry.children ?? [])]);

export const allDocs = flattenDocs(docsManifest);

export const findDoc = (slug: string) =>
  allDocs.find((doc) => doc.slug === slug);

export const docPath = (slug: string) => `${DOCS_BASE_PATH}/${slug}`;

export const adjacentDocs = (slug: string) => {
  const index = allDocs.findIndex((doc) => doc.slug === slug);
  return {
    prev: index > 0 ? allDocs[index - 1] : undefined,
    next:
      index >= 0 && index < allDocs.length - 1 ? allDocs[index + 1] : undefined,
  };
};

/**
 * Keeps an entry if its title matches or any descendant's does, so a match stays under its
 * parent in the tree.
 */
export const filterDocs = (entries: DocEntry[], query: string): DocEntry[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return entries;
  }
  return entries.flatMap((entry) => {
    const children = filterDocs(entry.children ?? [], needle);
    if (entry.title.toLowerCase().includes(needle) || children.length > 0) {
      return [entry.children ? { ...entry, children } : entry];
    }
    return [];
  });
};
